
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import ScheduleCalendar from "../components/schedule/ScheduleCalendar";
import { WorkDay } from "../types/schedule";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Plus } from "lucide-react";

const Schedule = () => {
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedWorkDay, setSelectedWorkDay] = useState<WorkDay | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Mock work days data
  const workDays: WorkDay[] = [
    {
      id: "1",
      date: "2025-05-15",
      shifts: [
        {
          id: "shift-1",
          role: "Assistant Teacher",
          startTime: "9:00 AM",
          endTime: "1:00 PM",
          assignee: {
            id: "1",
            name: "Sarah Johnson"
          }
        },
        {
          id: "shift-2",
          role: "Snack Helper",
          startTime: "10:30 AM",
          endTime: "12:30 PM",
          assignee: {
            id: "4",
            name: "David Kim"
          }
        }
      ]
    },
    {
      id: "2",
      date: "2025-05-20",
      shifts: [
        {
          id: "shift-3",
          role: "Assistant Teacher",
          startTime: "9:00 AM",
          endTime: "1:00 PM",
          assignee: {
            id: "3",
            name: "Emily Rodriguez"
          }
        },
        {
          id: "shift-4",
          role: "Art Project Helper",
          startTime: "10:00 AM",
          endTime: "12:00 PM",
          assignee: {
            id: "5",
            name: "Maria Wong"
          }
        },
        {
          id: "shift-5",
          role: "Cleanup Helper",
          startTime: "12:00 PM",
          endTime: "1:30 PM",
          assignee: {
            id: "6",
            name: "James Taylor"
          }
        }
      ]
    },
    {
      id: "3",
      date: "2025-05-22",
      shifts: [
        {
          id: "shift-6",
          role: "Assistant Teacher",
          startTime: "9:00 AM",
          endTime: "1:00 PM",
          assignee: {
            id: "1",
            name: "Sarah Johnson"
          }
        },
        {
          id: "shift-7",
          role: "Snack Helper",
          startTime: "10:30 AM",
          endTime: "12:30 PM"
        }
      ]
    }
  ];

  const handleDayClick = (date: Date, workDay?: WorkDay) => {
    setSelectedDay(date);
    setSelectedWorkDay(workDay || null);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Work Day Schedule</h1>
        <Button>
          <Plus className="h-4 w-4 mr-1" />
          Add Work Day
        </Button>
      </div>

      <ScheduleCalendar workDays={workDays} onDayClick={handleDayClick} />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedDay ? format(selectedDay, "EEEE, MMMM d, yyyy") : ""}
            </DialogTitle>
            <DialogDescription>
              {selectedWorkDay
                ? `${selectedWorkDay.shifts.length} shifts scheduled`
                : "No shifts scheduled for this day"}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {selectedWorkDay ? (
              <div className="space-y-4">
                {selectedWorkDay.shifts.map((shift) => (
                  <div
                    key={shift.id}
                    className="flex items-center justify-between border p-3 rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="font-medium">{shift.role}</div>
                      <div className="text-sm text-muted-foreground flex items-center">
                        <Clock className="h-3.5 w-3.5 mr-1" />
                        {shift.startTime} - {shift.endTime}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {shift.assignee ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-muted">
                              {shift.assignee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm hidden sm:inline">{shift.assignee.name}</span>
                        </div>
                      ) : (
                        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                          Available
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                <Button className="w-full">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Shift
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium">No Shifts Scheduled</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  There are no shifts scheduled for this day yet.
                </p>
                <Button>Create Work Day</Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schedule;
